import React from "react";
import { Button, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useCopyToClipboard from "../../AllMenusPage/hooks/useCopyToClipboard";
import { downloadQRCode } from "@me-team/host/src/utils/utils";
import { getDownloadableQrUrl } from "../../AllMenusPage/helpers";

interface QrMenuPreviewProps {
    menuType?: string;
    qrMenuLink?: string;
    qrMenuQrCodeLink?: string;
    isPublishedPage?: boolean;
}

const QrMenuPreview: React.FC<QrMenuPreviewProps> = ({ menuType, qrMenuQrCodeLink, qrMenuLink, isPublishedPage }) => {
    const { t } = useTranslation();
    const { copy, copiedText } = useCopyToClipboard();
    const basePath = process.env.REACT_APP_POS_URL;

    return (
        <>
            <Col xxl={10} className="py-4 my-6 mx-auto">
                <Row className="g-4">
                    <Col xs={12} lg={6}>
                        <div className="d-flex flex-column bg-white rounded-20 p-4 h-100">
                            <div className="mb-4 d-flex justify-content-center">
                                {isPublishedPage ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{t("Download QR code")}</Tooltip>}
                                    >
                                        <img
                                            src={qrMenuQrCodeLink}
                                            width="190"
                                            height="190"
                                            alt={t("QR code")}
                                            onClick={() =>
                                                downloadQRCode(getDownloadableQrUrl(qrMenuQrCodeLink, basePath))
                                            }
                                        />
                                    </OverlayTrigger>
                                ) : (
                                    <img src={qrMenuQrCodeLink} width="190" height="190" alt={t("QR code")} />
                                )}
                            </div>
                            <Col sm={10} xl={9} className="mx-sm-auto">
                                <p className="text-dark fs-6 text-center mb-0">
                                    {isPublishedPage
                                        ? t(
                                              "Display this QR code so your customers can scan it and view your {{menuType}}",
                                              { menuType: t(menuType) }
                                          )
                                        : t("Scan the QR code to view the mobile version")}
                                </p>
                            </Col>
                        </div>
                    </Col>
                    <Col xs={12} lg={6}>
                        <div className="d-flex flex-column gap-3 bg-white rounded-20 p-4 h-100">
                            <div className="bg-violet rounded-3 text-center px-2 py-5">
                                <a
                                    href={qrMenuLink}
                                    target="_blank"
                                    className="fs-20 text-primary py-2 text-center text-decoration-none"
                                >
                                    <span>{qrMenuLink}</span>
                                </a>
                                {isPublishedPage ? (
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip>{copiedText === qrMenuLink ? t("Copied") : t("Copy")}</Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="link"
                                            className="d-inline-block fs-16 text-primary p-0 pb-2 ms-2 flex-shrink-0"
                                            onClick={() => copy(qrMenuLink)}
                                        >
                                            <i className={copiedText === qrMenuLink ? "bi bi-check" : "bi bi-copy"} />
                                        </Button>
                                    </OverlayTrigger>
                                ) : null}
                            </div>
                            <Col sm={10} xl={9} className="mx-sm-auto">
                                <Button
                                    as="a"
                                    href={qrMenuLink}
                                    target="_blank"
                                    variant="primary"
                                    className="w-100 fs-7 fw-bold text-capitalize px-4 mb-3"
                                >
                                    {t("View menu")}
                                </Button>
                                <p className="text-dark fs-6 text-center mb-0">
                                    {isPublishedPage
                                        ? t("Share this link with your customers so they can view your {{menuType}}", {
                                              menuType: t(menuType),
                                          })
                                        : t("Follow the link to view the desktop version")}
                                </p>
                            </Col>
                        </div>
                    </Col>
                </Row>
            </Col>
        </>
    );
};

export default QrMenuPreview;
